import {
  COMMENT_TYPE,
  PROJECT_ICON_TYPE,
  SHOW_COMPLETED_TASK_BY_DATE,
  SHOW_TASK_BY_ORDER,
  SHOW_TASK_GROUPING_TYPE,
  TASK_LIST_TYPE
} from '../global/constants';
import { getColorIndex } from '../helpers/common';
import { PasswordDetailInterface } from '../interfaces/AuthInterface';
import { NewTaskFilterInterface } from '../interfaces/TaskInterface';
import { RawComment } from './interfaces/comment';
import { RawNotification } from './interfaces/notification';
import { Icon, RawIcon, RawProject, RawProjectTemplate } from './interfaces/project';
import { RawTask } from './interfaces/task';
import { RawView } from './interfaces/view';
import { NotificationModel } from './models/notification';
import { ProjectModel } from './models/project';
import { ProjectTemplateModel } from './models/projectTemplate';
import { TaskModel } from './models/task';
import { ViewModel } from './models/view';
import { rootStore } from './rootStore';
import { TaskTemplateModel } from './stores/projectTemplateStore';

type RawItem = RawTask | RawProject | RawNotification;
type ObservableItem = TaskModel | ProjectModel | NotificationModel;
export type GenericUpsertOptions<T extends ObservableItem> = { excludeFields?: (keyof T)[]; currentId?: string };

/**
 * Generic function to upsert items in a store
 * @param items Current items in the store
 * @param rawItems New raw items to upsert
 * @param createObservableItem Function to create an observable item from raw data
 * @param additionalUpdateLogic Optional function to apply additional update logic
 * @returns Object containing new and updated items
 */
export function genericUpsert<T extends ObservableItem, R extends RawItem>(
  items: T[],
  rawItems: R[],
  createObservableItem: (rawItem: R) => T,
  { excludeFields, currentId }: GenericUpsertOptions<T> = {}
) {
  let updatedCurrentItem: T | null = null;
  const indexMap = new Map<string, number>(items.map((item, index) => [(item as any).id, index]));

  const newItems: T[] = [];
  const updatedItems: T[] = [];

  for (const rawItem of rawItems) {
    const itemIndex = indexMap.get(rawItem.id);
    if (typeof itemIndex === 'number') {
      const updatedItem = createObservableItem(rawItem);

      if (excludeFields) {
        excludeFields.forEach((field) => {
          updatedItem[field] = items[itemIndex][field];
        });
      }

      items[itemIndex] = updatedItem;
      updatedItems.push(updatedItem);

      if (currentId && currentId === rawItem.id) {
        updatedCurrentItem = updatedItem;
      }
    } else {
      const newItem = createObservableItem(rawItem);
      newItems.push(newItem);
    }
  }

  // Push all new items at once
  if (newItems.length > 0) {
    items.push(...newItems);
  }

  return { newItems, updatedItems, updatedCurrentItem };
}

/**
 * Generic function to remove items from a store
 * @param items Current items in the store
 * @param itemIds IDs of items to remove
 * @returns The filtered array of items
 */
export function genericRemove<T extends ObservableItem>(items: T[], itemIds: string[]): T[] {
  return items.filter((item) => !itemIds.includes((item as any).id));
}

export const formatToRawTask = (item: any) => {
  const workspaceId = rootStore.workspaceStore.currentWorkspace?.id;

  let users = [];
  if (item?.TaskUsers instanceof Array && item.TaskUsers.length > 0) {
    if (typeof item.TaskUsers[0] === 'string') {
      users = item?.TaskUsers?.filter((userId?: string) => !!userId);
    }

    if (typeof item.TaskUsers[0] === 'object') {
      users = item?.TaskUsers?.map((user: any) => user['_id']).filter((userId?: string) => !!userId);
    }
  }

  const newTask: RawTask = {
    id: item['_id'],
    comments: item.Comments ? item.Comments.map(formatToRawComment) : [],
    companyId: workspaceId || '',
    createdOn: item?.CreatedTime,
    createdBy: item?.CreatedBy,
    groupId: item?.GroupId,
    index: item?.Index,
    isArchived: item?.IsArchived === true ? true : false,
    description: item?.Desc || '',
    name: item?.Name,
    projectId: item?.ProjectId,
    status: item?.Status,
    dueDate: item?.DueDate,
    files: item?.Files || [],
    priority: item?.Priority,
    users,
    taskSubscribers: item?.Subscribers,
    estimate: item?.EstimateTime,
    labels: item?.Labels,
    milestoneId: item?.MilestoneId,
    parentTaskId: item?.ParentTaskId,
    statusId: item?.StatusId,
    updatedTime: item?.UpdatedTime,
    updatedBy: item?.UpdatedBy || item.CreatedBy,
    subtaskIndex: item?.SubtaskIndex,
    taskNumber: item?.TaskNumber,
    pullRequests: item?.PullRequests || [],
    links: item?.Links || [],
    recurringInterval: item?.recurringInterval || null,
    start: null
  };

  return newTask;
};

export const formatToRawComment = (item: any) => {
  const assigneeId = [COMMENT_TYPE.ADD_ASSIGNEE, COMMENT_TYPE.REMOVE_ASSIGNEE].includes(item.Type)
    ? item.Assignee
    : null;

  const formattedComment: RawComment = {
    id: item['_id'],
    comment: item.Comment,
    createdOn: item.CreatedTime,
    createdBy: item.CreatedBy,
    updatedBy: item.UpdatedBy || item.CreatedBy,
    updatedTime: item.UpdatedTime,
    type: item.Type || 0,
    dueDate: item.DueDate,
    previousDueDate: item.PreviousDueDate,
    assigneeId,
    previousPriority: item.PreviousPriority,
    priority: item.Priority,
    isArchived: item.IsArchived,
    previousGroup: item.PreviousGroup,
    group: item.Group,
    status: item.Status,
    estimateTime: item.EstimateTime,
    reactions: item.Reactions || [],
    Files: item.Files,
    reply: item.Reply?.id,
    isGithub: item.IsGithub
  };

  return formattedComment;
};

export const formatTemplateTasksToRawTasks = (tasks: any): RawTask[] => {
  return tasks?.map((task: any) => {
    return {
      id: task?.id,
      name: task?.name,
      description: task?.description,
      isArchived: task?.isArchived,

      companyId: task?.companyId,
      createdBy: task?.createdBy,
      createdOn: task?.createdOn,
      updatedBy: task?.updatedBy,
      updatedTime: task?.updatedOn,

      projectId: task?.projectId,
      projectName: task?.projectName,

      users: task?.taskUsers?.map((user: any) => user['_id']) || [],

      status: task?.status,
      statusId: task?.statusId,
      groupId: task?.groupId,
      labels: task?.labels || [],
      priority: task?.priority ?? 0,
      estimate: task?.estimate,
      files: task?.files || [],
      taskSubscribers: task?.taskSubscribers || [],
      parentTaskId: task?.parentTaskId || null,

      // Additional required RawTask properties
      dueDate: null,
      milestoneId: null,
      index: 0,
      comments: [],
      subtaskIndex: 0,
      taskNumber: 0,
      pullRequests: [],
      timeLogs: [],
      recurringInterval: null,
      start: null
    };
  });
};
export const formatToProjectTemplateStore = (data: any): RawProjectTemplate[] => {
  return data.map((x: any) => {
    const groups = x?.groups?.map((group: any) => {
      return {
        id: group?.id,
        name: group?.name,
        projectId: group?.projectId,
        index: group?.index,
        isCollapse: group?.isCollapse ?? false
      };
    });
    const userIds = x?.projectUsers?.map((user: any) => {
      return user['_id'];
    });

    const tasks = formatTemplateTasksToRawTasks(x?.tasks);
    const labelList = x?.labelsList?.map((label: any) => {
      return {
        id: label?.id || label?.['_id'],
        name: label.name,
        color: label.color,
        createdBy: x?.createdBy,
        createdTime: x?.createdTime,
        updatedBy: x?.updatedBy,
        updatedTime: x?.updatedTime
      };
    });
    return {
      id: x?.['_id'],
      name: x?.name,
      description: x?.description,
      companyId: x?.companyId,
      createdBy: x?.createdBy,
      createdTime: x?.createdTime,
      updatedBy: x?.updatedBy,
      updatedTime: x?.updatedTime,
      isArchived: x?.isArchived ?? false,
      value: x?.name,
      updated: x?.updated ?? false,
      isEstimate: x?.isEstimate ?? false,
      isMilestone: x?.isMilestone ?? false,
      multiAssignee: x?.multiAssignee ?? false,
      priority: x?.priority ?? { enabled: false, default: 0 },
      statusData: x?.statusData ?? [],
      statusEnable: x?.statusEnable ?? false,
      labelsEnabled: x?.labelsEnabled ?? false,
      labelsList: labelList ?? [],
      isGroupEnabled: x?.isGroupEnabled ?? false,
      groups: groups ?? [],

      userIds: userIds ?? [],
      tasks: tasks ?? []
    };
  });
};

export const convertTaskModelToTask = (taskModel: TaskModel) => {
  const taskUsers = taskModel.users.map((user) => {
    return {
      _id: user?.id,
      CreatedBy: user?.id,
      CreatedTime: new Date(),
      UpdatedBy: user?.id,
      UpdatedTime: new Date()
    };
  });
  return {
    id: taskModel.id,
    companyId: taskModel.companyId,
    createdOn: taskModel.createdOn.toISOString(),
    createdBy: taskModel.createdBy,
    updatedOn: taskModel.updatedTime?.toISOString() || '',
    updatedBy: taskModel.updatedBy,
    groupId: taskModel.groupId || '',
    isArchived: taskModel.isArchived,
    description: taskModel.description,
    name: taskModel.name,
    projectId: taskModel.projectId,
    projectName: rootStore?.projectTemplateStore?.currentProjectTemplate?.name || '',
    status: taskModel.status,
    files: taskModel.files.map((file) => ({
      file_type: file.file_type || '',
      href: file.href || '',
      name: file.name || '',
      size: file.size?.toString() || '',
      uploadFileType: file.uploadFileType || 0
    })),
    priority: taskModel.priority || undefined,
    taskUsers: taskUsers || [],
    taskSubscribers: taskModel.taskSubscribers,
    estimate: taskModel.estimate || undefined,
    labels: taskModel.labelsIds || [],
    statusId: taskModel.statusId
  };
};

export const convertProjectTemplateModelToInterface = (templateModel: ProjectTemplateModel) => {
  const currentUser = rootStore?.currentUser;
  const projectUsers = templateModel.users.map((user) => {
    return {
      _id: user?.id,
      CreatedBy: currentUser?.id,
      CreatedTime: new Date(),
      UpdatedBy: currentUser?.id,
      UpdatedTime: new Date()
    };
  });
  return {
    _id: templateModel.id,
    id: templateModel.id,
    name: templateModel.name,
    companyId: templateModel.companyId,
    description: templateModel.description,
    projectUsers: projectUsers || [],
    isEstimate: templateModel.isEstimate,
    isMilestone: templateModel.isMilestone,
    isArchived: templateModel.isArchived,
    priority: templateModel.priority,
    tasks: templateModel.tasks.map((task) => convertTaskModelToTask(task)),
    groups: templateModel.taskGroups.map((group) => ({
      _id: group.id,
      name: group.name,
      projectId: group.projectId,
      createdBy: currentUser?.id,
      createdTime: new Date(),
      updatedBy: currentUser?.id,
      updatedTime: new Date(),
      index: group.index,
      id: group.id
    })),
    labelsEnabled: templateModel.labelsEnabled,
    labelsList: templateModel.labelsList.map((label) => ({
      id: label?.id,
      name: label.name,
      color: label.color,
      createdBy: currentUser?.id,
      createdTime: new Date(),
      updatedBy: currentUser?.id,
      updatedTime: new Date()
    })),
    createdBy: templateModel.createdBy,
    createdTime: templateModel.createdTime,
    updatedBy: templateModel.updatedBy,
    updatedTime: templateModel.updatedTime || null,
    statusData: templateModel.statusData,
    statusEnable: templateModel.statusEnable,
    defaultStatus: templateModel.defaultStatus,
    updated: templateModel.updated,
    isGroupEnabled: templateModel.isGroupEnabled,
    multiAssignee: templateModel.multiAssignee
  };
};

export const formatToTaskTemplateStore = (data: any[]): TaskTemplateModel[] => {
  return data.map((item: any) => {
    const task = new TaskTemplateModel();

    task.id = item['_id'] || '';
    task.name = item?.TaskTitle ?? '';
    task.description = item?.Description ?? '';
    task.priority = item?.Priority ?? null;
    task.createdBy = item?.CreatedBy ?? '';
    task.createdOn = item?.CreatedTime ?? '';
    task.files = item?.Attachment ?? [];
    task.usersIds = item?.Assignee ?? [];
    task.taskUsers = item?.Assignee ?? [];
    task.templateName = item?.Title ?? '';

    return task;
  });
};

export const formatToViewStore = (data: any[]): ViewModel[] => {
  return data.map((item: any) => {
    const formattedItem: RawView = {
      id: item?.['_id'] || '',
      name: item?.Name ?? '',
      description: item?.Description ?? '',
      icon: item?.Icon ?? '',
      color: item?.Color ?? '',
      userIds: item?.Users ?? [],
      filters: item?.Filters ?? {},
      createdById: item?.CreatedBy ?? '',
      createdOn: new Date(item?.CreatedOn),
      updatedById: item?.UpdatedBy ?? '',
      updatedOn: new Date(item?.UpdatedOn)
    };

    return new ViewModel(formattedItem);
  });
};

export const formatToIcon = (projectName: string, data?: RawIcon): Icon | null => {
  return {
    type: data?.Type || PROJECT_ICON_TYPE.DEFAULT,
    reference: data?.Reference,
    color: data?.Color || getColorIndex(projectName)
  };
};

export const defaultDisplayFilters: NewTaskFilterInterface = {
  showSubTask: false,
  showCompletedTaskByFilter: SHOW_COMPLETED_TASK_BY_DATE.LAST_MONTH,
  showRecentlyCompletedTask: true,
  showEmptyGroup: false,
  taskViewType: TASK_LIST_TYPE.TASKS,
  groupingStatus: SHOW_TASK_GROUPING_TYPE.GROUPING_BY_STATUS,
  orderByStatus: SHOW_TASK_BY_ORDER.CUSTOM
};

export const DefaultPasswordDetail: PasswordDetailInterface = {
  oldPassword: '',
  newPassword: '',
  confirmPassword: ''
};
